// src/pages/ChatWithAI/components/dialogs/AnexarOneDriveDialog.js
import React, { useState, useMemo } from 'react';
import axios from 'axios';
import './AnexarDialog.css';

const INTERVALO_POLLING_MS = 3000;
const MAX_TENTATIVAS_POLLING = 60; // ~3 minutos por ficheiro

const aguardar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// O Graph do OneDrive endereça por "/" — aceitamos caminhos colados com "\"
// (como aparecem no sistema Forense) e normalizamos para "/"
const normalizarCaminho = (caminho) => (caminho || '').trim().replace(/\\/g, '/');

// Deriva o nome real (com extensão) a partir do caminho
const obterNomeDoCaminho = (caminho) => {
  const segmentos = normalizarCaminho(caminho).split('/');
  return segmentos[segmentos.length - 1] || 'ficheiro';
};

// Traduz erros técnicos numa mensagem amigável para o utilizador
const mensagemAmigavelErro = (err, fallback) => {
  const status = err.response?.status;
  const msgServidor = err.response?.data?.message;
  if (status === 404) {
    return 'Ficheiro não encontrado no OneDrive. Verifique o caminho indicado.';
  }
  if (msgServidor) return msgServidor;
  return err.message || fallback;
};

// Analisa o texto colado: um caminho por linha, remove numeração ("1." / "2)" / "3]")
// e normaliza "\"→"/". Remove duplicados preservando a ordem.
const parseCaminhos = (texto) => {
  const linhas = (texto || '')
    .split(/\r?\n/)
    .map((linha) => linha.trim())
    .filter(Boolean)
    .map((linha) => linha.replace(/^\d+[.)\]]\s*/, ''))
    .map(normalizarCaminho)
    .filter(Boolean);

  return Array.from(new Set(linhas));
};

/**
 * Diálogo para anexar um ou mais ficheiros directamente do OneDrive à conversa.
 * Aceita uma lista de caminhos (um por linha, com ou sem numeração), transcreve
 * cada ficheiro (texto imediato para pdf/docx/txt com texto; OCR assíncrono via
 * Document AI para digitalizados — como na tela de dados de cliente) e devolve
 * todos os textos transcritos como anexos através de onAnexar.
 *
 * Props:
 *   onClose  — () => void
 *   onAnexar — (anexos: [{ nome: string, conteudo: string }]) => Promise<void>
 */
const AnexarOneDriveDialog = ({ onClose, onAnexar }) => {
  const [texto, setTexto] = useState('');
  const [processando, setProcessando] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [error, setError] = useState(null);
  const [itens, setItens] = useState([]); // [{ caminho, nome, status, erro }]

  const caminhos = useMemo(() => parseCaminhos(texto), [texto]);

  // Lista mostrada: itens processados (com estado) ou pré-visualização dos detectados
  const listaMostrada = useMemo(() => {
    if (itens.length) return itens;
    return caminhos.map((caminho) => ({
      caminho,
      nome: obterNomeDoCaminho(caminho),
      status: 'pending',
      erro: null
    }));
  }, [itens, caminhos]);

  const handleTextoChange = (e) => {
    setTexto(e.target.value);
    setItens([]);
    setError(null);
  };

  const atualizarItem = (caminho, patch) =>
    setItens((prev) => prev.map((it) => (it.caminho === caminho ? { ...it, ...patch } : it)));

  // Transcreve um único ficheiro e devolve o texto (lança em caso de erro)
  const transcreverUm = async (caminho, nome) => {
    const response = await axios.post('/api/onedrive/transcrever', { caminho, nome });
    const resultado = response.data;

    if (!resultado.success) {
      throw new Error(resultado.message || 'Erro ao transcrever ficheiro');
    }

    if (!resultado.async) {
      if (!resultado.texto || !resultado.texto.trim()) {
        throw new Error('Ficheiro sem texto extraível.');
      }
      return resultado.texto;
    }

    // Ficheiro digitalizado — polling ao job assíncrono do Document AI
    let tentativas = 0;
    while (tentativas < MAX_TENTATIVAS_POLLING) {
      await aguardar(INTERVALO_POLLING_MS);
      tentativas += 1;

      const statusResponse = await axios.get('/api/onedrive/transcrever/status', {
        params: { jobId: resultado.jobId, operationName: resultado.operationName }
      });
      const statusData = statusResponse.data;

      if (!statusData.success) {
        throw new Error(statusData.message || 'Erro ao transcrever ficheiro digitalizado');
      }

      if (statusData.done) {
        if (!statusData.texto || !statusData.texto.trim()) {
          throw new Error('Ficheiro sem texto extraível.');
        }
        return statusData.texto;
      }
    }

    throw new Error('A transcrição está a demorar demasiado tempo. Tente novamente mais tarde.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!caminhos.length) {
      setError('Cole pelo menos um caminho de ficheiro do OneDrive (um por linha)');
      return;
    }

    setProcessando(true);
    setError(null);

    const iniciais = caminhos.map((caminho) => ({
      caminho,
      nome: obterNomeDoCaminho(caminho),
      status: 'pending',
      erro: null
    }));
    setItens(iniciais);

    const sucessos = [];
    const sucessoCaminhos = new Set();

    for (let i = 0; i < iniciais.length; i += 1) {
      const item = iniciais[i];
      setStatusMsg(`A transcrever ${i + 1}/${iniciais.length}: ${item.nome}`);
      atualizarItem(item.caminho, { status: 'processing' });

      try {
        const conteudo = await transcreverUm(item.caminho, item.nome);
        sucessos.push({ nome: item.nome, conteudo });
        sucessoCaminhos.add(item.caminho);
        atualizarItem(item.caminho, { status: 'done' });
      } catch (err) {
        console.error('❌ Erro ao transcrever', item.caminho, err);
        atualizarItem(item.caminho, {
          status: 'error',
          erro: mensagemAmigavelErro(err, 'Erro ao transcrever')
        });
      }
    }

    setStatusMsg('');

    if (sucessos.length === 0) {
      setError('Nenhum ficheiro foi transcrito com sucesso. Verifique os caminhos.');
      setProcessando(false);
      return;
    }

    try {
      setStatusMsg('A anexar à conversa...');
      await onAnexar(sucessos);

      const falhas = iniciais.length - sucessos.length;
      if (falhas === 0) {
        onClose();
        return;
      }

      // Sucesso parcial: mantém a dialog aberta e deixa no campo apenas os que
      // falharam, para o utilizador poder tentar novamente só esses.
      const caminhosFalhados = iniciais
        .filter((it) => !sucessoCaminhos.has(it.caminho))
        .map((it) => it.caminho);
      const falhadosSet = new Set(caminhosFalhados);
      setTexto(caminhosFalhados.join('\n'));
      setItens((prev) => prev.filter((it) => falhadosSet.has(it.caminho)));
      setError(`${sucessos.length} ficheiro(s) anexado(s). ${falhas} com erro — reveja os caminhos e tente novamente.`);
    } catch (err) {
      setError(mensagemAmigavelErro(err, 'Erro ao anexar ficheiros'));
    } finally {
      setProcessando(false);
      setStatusMsg('');
    }
  };

  const renderBadge = (status) => {
    if (status === 'processing') return <span className="od-badge od-badge-proc">⏳ A processar</span>;
    if (status === 'done') return <span className="od-badge od-badge-done">✅ Concluído</span>;
    if (status === 'error') return <span className="od-badge od-badge-err">⚠️ Erro</span>;
    return <span className="od-badge od-badge-pend">• Pendente</span>;
  };

  return (
    <div className="anexar-dialog-overlay" onClick={processando ? undefined : onClose}>
      <div className="anexar-dialog anexar-dialog-large" onClick={(e) => e.stopPropagation()}>
        <div className="anexar-dialog-header">
          <h3>☁️ Anexar Ficheiro(s) OneDrive</h3>
          <button onClick={onClose} className="anexar-dialog-close" disabled={processando}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="anexar-dialog-content">
          <div className="form-group">
            <label>Caminhos dos ficheiros no OneDrive (um por linha)</label>
            <textarea
              className="od-paths-input"
              rows={7}
              placeholder={
                'Cole aqui um ou mais caminhos, um por linha. Ex:\n' +
                '1. Clientes_arquivados/08520-Pedro_Ventura/.../ficheiro.pdf\n' +
                '2. Clientes_arquivados/08520-Pedro_Ventura/.../outro.pdf'
              }
              value={texto}
              onChange={handleTextoChange}
              disabled={processando}
              autoFocus
            />
            <small className="input-hint">
              Um caminho por linha (a coluna "Caminho" mostrada em Dados do Cliente). A numeração "1.", "2." é ignorada. Suporta PDF, DOCX e TXT; digitalizados passam por OCR.
            </small>
          </div>

          {listaMostrada.length > 0 && (
            <div className="od-file-list">
              <div className="od-file-list-header">
                {caminhos.length} ficheiro(s) detectado(s)
              </div>
              {listaMostrada.map((it) => (
                <div key={it.caminho} className={`od-file-row od-file-row-${it.status}`}>
                  <span className="od-file-nome" title={it.caminho}>📄 {it.nome}</span>
                  {renderBadge(it.status)}
                  {it.erro && <span className="od-file-erro">{it.erro}</span>}
                </div>
              ))}
            </div>
          )}

          {processando && statusMsg && (
            <div className="anexar-hint">
              ⏳ {statusMsg}
            </div>
          )}

          {error && (
            <div className="anexar-error">
              ⚠️ {error}
            </div>
          )}

          <div className="anexar-hint">
            <strong>💡 Como funciona:</strong><br />
            1. Cole os caminhos dos ficheiros (um por linha)<br />
            2. Clique em "Anexar" — cada ficheiro é transcrito e adicionado ao contexto<br />
            3. O progresso é mostrado por ficheiro; erros num ficheiro não param os restantes
          </div>

          <div className="anexar-dialog-actions">
            <button
              type="button"
              onClick={onClose}
              className="anexar-btn-cancel"
              disabled={processando}
            >
              {processando ? 'A processar...' : 'Fechar'}
            </button>
            <button
              type="submit"
              className="anexar-btn-submit"
              disabled={!caminhos.length || processando}
            >
              {processando
                ? 'A transcrever...'
                : `Anexar${caminhos.length > 0 ? ` (${caminhos.length})` : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnexarOneDriveDialog;
